import {
  HeadlessFlatTreeItemProps,
  makeStyles,
  TreeItemValue,
  useHeadlessFlatTree_unstable,
} from "@fluentui/react-components";
import * as React from "react";
import { useEffect, useRef } from "react";
import { PageDocumentRoots } from "../../../api/models/page-document-roots";
import { FlatPathTreeItem } from "../FlatPathTreeExplorer";
import { DocumentPart } from "../../../api/models";
import { DocumentRoot } from "../../../api/models/document-root";

type CustomItem = HeadlessFlatTreeItemProps & {
  name: string | React.ReactNode;
  document: DocumentPart;
  permission: boolean;
};

const useStyles = makeStyles({
  container: {
    height: "65vh",
    overflow: "auto",
    fontSize: "1.25rem",
    fontWeight: "600",
    lineHeight: "1.25rem",
  },
  screenReadersOnly: {
    position: "relative",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
  },
  fileIconHeaderIcon: {
    padding: 0,
    margin: "9px 10px 0",
    fontSize: "1.25rem",
    color: "#007AC3",
  },
  rowTreeItemBlack: {
    display: "flex",
    color: "var(--color-black)",
    "&:hover, &:focus": {
      backgroundColor: "#e6f2f9",
    },
  },
  rowTreeItemGray: {
    display: "flex",
    opacity: "0.5",
    "&:hover, &:focus": {
      backgroundColor: "#e6f2f9",
    },
  },
  documentName: {
    "&>div": {
      maxWidth: "68vw",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
});

export interface VirtualTreeProps {
  childrenItems: FlatPathTreeItem[];
  loadDocumentPart: (documentRoot: DocumentRoot) => DocumentRoot;
  openDocument: (documentPart: DocumentPart) => DocumentPart;
  virtualScrollAddItems: (page: number) => void;
  pagination: PageDocumentRoots;
  firstNewItemValue: string;
  selectedItem?: FlatPathTreeItem;
  showDownloadButton?: boolean;
}

const useVirtualTree = (props: VirtualTreeProps) => {
  const { childrenItems, pagination, firstNewItemValue, selectedItem } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const itemToFocusRef = React.useRef<HTMLDivElement>(null);
  const [itemToFocusValue, setItemToFocusValue] = React.useState<TreeItemValue>();
  const items = React.useMemo<CustomItem[]>(() => refreshItems(), [isLoading, childrenItems]);
  const styles = useStyles();
  const flatTree = useHeadlessFlatTree_unstable(items);
  const FlatTreeRef = useRef(null);

  useEffect(() => {
    if (
      FlatTreeRef.current.scrollHeight <= FlatTreeRef.current.clientHeight &&
      pagination &&
      pagination.number + 1 < pagination.totalPages
    ) {
      // eslint-disable-next-line no-undef
      console.log("flat treee ");
      fetchMoreItems();
    }
  }, [pagination?.number]);

  useEffect(() => {
    setItemToFocusValue(firstNewItemValue);
  }, [firstNewItemValue]);

  useEffect(() => {
    if (itemToFocusRef.current) {
      itemToFocusRef.current.focus();
      setItemToFocusValue(undefined);
    }
  }, [itemToFocusValue]);

  function refreshItems() {
    return [
      ...childrenItems.map((x) => {
        return { value: x.key, name: x.text, document: x.document, permission: x.readPermission };
      }),
    ];
  }

  function handleClick(documentRoot: DocumentRoot) {
    props.loadDocumentPart(documentRoot);
  }
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const hasReachedEnd = scrollHeight - scrollTop <= clientHeight + 5;
    if (!isLoading && hasReachedEnd && pagination?.number + 1 < pagination?.totalPages) {
      fetchMoreItems();
    }
  };

  const fetchMoreItems = async () => {
    setIsLoading(true);
    props.virtualScrollAddItems(pagination.number + 1);
    setIsLoading(false);
  };

  return {
    flatTree,
    handleScroll,
    styles,
    handleClick,
    itemToFocusValue,
    itemToFocusRef,
    selectedItem,
    FlatTreeRef,
  };
};

export default useVirtualTree;
